/**
 * Implementacion del `PuertoReservas` sobre las Cloud Functions callable.
 *
 * Lo unico que hace este modulo es hablar con `consultarDisponibilidad`,
 * `buscarClientePublico` y `crearReservaPublica` (docs/API.md), y **validar la respuesta
 * con Zod**. Lo que llega
 * de la red es `unknown` y se estrecha; no se castea (CLAUDE.md §1.1). Una respuesta que
 * no cumple el contrato es `RESPUESTA_INVALIDA`, no un objeto a medio construir que
 * reviente tres componentes mas adelante.
 *
 * Toda la logica de negocio —precio, fin, ventana, disponibilidad— la decide el
 * servidor. Aca no se calcula ninguna.
 */

import { httpsCallable } from 'firebase/functions';
import { z } from 'zod';

import { CANCHAS, DURACIONES_MINUTOS } from '@padel/domain';

import type {
  ConsultaClientePublico,
  ConsultaDisponibilidad,
  Disponibilidad,
  PuertoReservas,
  ReservaCreada,
  ResultadoBusquedaClientePublico,
  SolicitudReservaPublica,
} from '@/infra/contratos';
import { ErrorPublico, traducirError } from '@/infra/errores';
import type { ServiciosFirebase } from '@/infra/firebase';

const esquemaDuracion = z.union([
  z.literal(DURACIONES_MINUTOS[0]),
  z.literal(DURACIONES_MINUTOS[1]),
]);

const esquemaCanchaDisponibilidad = z
  .object({
    canchaId: z.enum(CANCHAS),
    estado: z.enum(['disponible', 'proximamente']).optional(),
    dias: z.array(
      z.object({
        localFecha: z.string(),
        inicios: z.array(
          z.object({
            localHora: z.string(),
            inicioUtc: z.string(),
            opciones: z.array(
              z.object({
                duracionMinutos: esquemaDuracion,
                precioCentesimos: z.number().int().nonnegative(),
                disponible: z.boolean(),
              }),
            ),
          }),
        ),
      }),
    ),
  })
  .transform((cancha) => ({
    ...cancha,
    estado: cancha.estado ?? 'disponible',
  }));

const esquemaDisponibilidad = z.object({
  desdeLocalFecha: z.string(),
  hastaLocalFecha: z.string(),
  zonaHoraria: z.string(),
  canchas: z.array(esquemaCanchaDisponibilidad),
  tarifas: z
    .array(
      z.object({
        desdeLocalHora: z.string(),
        hastaLocalHoraExclusiva: z.string(),
        opciones: z.array(
          z.object({
            duracionMinutos: esquemaDuracion,
            precioCentesimos: z.number().int().nonnegative(),
          }),
        ),
      }),
    )
    .default([]),
});

const esquemaReservaCreada = z.object({
  reservaId: z.string().min(1),
  inicioUtc: z.string().min(1),
  finUtc: z.string().min(1),
  inicioLocalFecha: z.string().min(1),
  inicioLocalHora: z.string().min(1),
  cruzaMedianoche: z.boolean(),
  precioCentesimos: z.number().int().nonnegative(),
  repetida: z.boolean(),
  datosTransferencia: z
    .object({
      banco: z.string().min(1),
      numeroCuenta: z.string().min(1),
      nombreTitular: z.string().min(1),
      telefonoComprobante: z.string().min(1),
      telefonoWhatsapp: z.string().regex(/^\d{8,15}$/u),
    })
    .strict()
    .optional(),
});

const esquemaBusquedaClientePublico = z.discriminatedUnion('encontrado', [
  z.object({ encontrado: z.literal(false) }).strict(),
  z
    .object({
      encontrado: z.literal(true),
      cliente: z
        .object({
          nombre: z.string().min(1).regex(/\*/u),
          apellido: z.string().min(1).regex(/\*/u),
          telefono: z.string().regex(/^\d{3}\*+\d{2}$/u),
        })
        .strict(),
    })
    .strict(),
]);

/** Estrecha `unknown` o falla con `RESPUESTA_INVALIDA`. */
function validar<T>(esquema: z.ZodType<T>, bruto: unknown): T {
  const analizado = esquema.safeParse(bruto);
  if (!analizado.success) {
    throw new ErrorPublico('RESPUESTA_INVALIDA');
  }
  return analizado.data;
}

/** Auth se obtiene solo cuando una callable pública necesita su sesión anónima. */
async function asegurarSesionAnonima(servicios: ServiciosFirebase): Promise<void> {
  const { asegurarSesion } = await import('@/infra/firebaseAuth');
  await asegurarSesion(servicios);
}

/**
 * Construye el puerto real.
 *
 * Se inyecta desde `main.tsx`. Las pruebas de componentes inyectan un doble en memoria,
 * de modo que el flujo publico entero se puede probar sin emuladores ni red.
 */
export function puertoFirebase(servicios: ServiciosFirebase): PuertoReservas {
  // La respuesta se declara `unknown` —el valor por defecto del SDK— porque lo que llega
  // de la red no es de fiar hasta que `validar` lo estreche con Zod.
  const llamarDisponibilidad = httpsCallable<ConsultaDisponibilidad>(
    servicios.funciones,
    'consultarDisponibilidad',
  );
  const llamarBuscarCliente = httpsCallable<ConsultaClientePublico>(
    servicios.funciones,
    'buscarClientePublico',
  );
  const llamarCrearReserva = httpsCallable<SolicitudReservaPublica>(
    servicios.funciones,
    'crearReservaPublica',
  );

  return {
    async consultarDisponibilidad(entrada: ConsultaDisponibilidad): Promise<Disponibilidad> {
      try {
        await asegurarSesionAnonima(servicios);
        const respuesta = await llamarDisponibilidad(entrada);
        return validar(esquemaDisponibilidad, respuesta.data);
      } catch (causa) {
        throw traducirError(causa);
      }
    },

    async buscarClientePublico(
      entrada: ConsultaClientePublico,
    ): Promise<ResultadoBusquedaClientePublico> {
      try {
        await asegurarSesionAnonima(servicios);
        const respuesta = await llamarBuscarCliente(entrada);
        return validar(esquemaBusquedaClientePublico, respuesta.data);
      } catch (causa) {
        throw traducirError(causa);
      }
    },

    async crearReservaPublica(entrada: SolicitudReservaPublica): Promise<ReservaCreada> {
      try {
        await asegurarSesionAnonima(servicios);
        const respuesta = await llamarCrearReserva(entrada);
        return validar(esquemaReservaCreada, respuesta.data);
      } catch (causa) {
        throw traducirError(causa);
      }
    },
  };
}
